import { createAsyncThunk } from "@reduxjs/toolkit"
import { type BaseOptionType } from "antd/lib/select"

import { errorSave } from "../../../../redux/errors.slice"
import { type BasePagination } from "../../../../types/api.ts"
import { unknownError } from "../../../../utils/handlers/handle-error"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import * as services from "../../api/project-invoices.services"
import {
  type InvoiceSnapshot,
  type ProjectInvoice
} from "../../types/project-invoices.types.ts"
import { projectSliceName } from "../project.constants.ts"
import { type ProjectInvoiceFilters } from "../project.types.ts"

export const fetchInvoices = createAsyncThunk<
  { isMore: boolean; data: BasePagination<ProjectInvoice> },
  {
    params: ProjectInvoiceFilters & { project: string; ordering: string | null }
    isMore: boolean
  }
>(
  `${projectSliceName}/fetchInvoices`,
  async ({ params, isMore }, { dispatch, rejectWithValue }) => {
    const response = await services.getInvoices(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return { data: response.data as BasePagination<ProjectInvoice>, isMore }
  }
)

export const exportInvoices = createAsyncThunk<Blob, string>(
  `${projectSliceName}/exportInvoices`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.exportInvoices(projectId)

    if (!response.ok) {
      const errorMessage =
        response?.data instanceof Blob
          ? unknownError
          : response?.data?.details || unknownError

      dispatch(errorSave(errorMessage))
      return rejectWithValue(errorMessage)
    }

    return response.data as Blob
  }
)

export const createInvoice = createAsyncThunk<
  ProjectInvoice,
  Partial<ProjectInvoice> & {
    silent?: boolean
    suppressError?: boolean
    lines?: Array<{ finance_line: string; amount: string }>
  }
>(
  `${projectSliceName}/createInvoice`,
  async ({ silent, suppressError, ...data }, { dispatch, rejectWithValue }) => {
    const response = await services.createInvoice(data)

    if (!response.ok) {
      if (!suppressError) dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    if (!silent) showResponseMessage(response, "Invoice created successfully.")

    return (response?.data?.details?.data ?? response?.data) as ProjectInvoice
  }
)

export const patchInvoice = createAsyncThunk<
  ProjectInvoice,
  Partial<ProjectInvoice> & {
    id: string
    silent?: boolean
    suppressError?: boolean
    lines?: Array<{ finance_line: string; amount: string }>
  }
>(
  `${projectSliceName}/patchInvoice`,
  async ({ silent, suppressError, ...data }, { dispatch, rejectWithValue }) => {
    const response = await services.patchInvoice(data)

    if (!response.ok) {
      if (!suppressError) {
        dispatch(errorSave(response?.data?.details || unknownError))
      }
      return rejectWithValue(response.data)
    }

    if (!silent) showResponseMessage(response, "Invoice updated successfully.")
    const res = (response?.data?.details?.data ??
      response?.data) as ProjectInvoice

    return { ...res, id: data?.id }
  }
)

export const fetchInvoicesFilterOptions = createAsyncThunk<
  Record<string, BaseOptionType[]>,
  { project_id: string }
>(
  `${projectSliceName}/fetchInvoicesFilterOptions`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getInvoicesFilterOptions(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as Record<string, BaseOptionType[]>
  }
)

export const deleteInvoice = createAsyncThunk<string, string>(
  `${projectSliceName}/deleteInvoice`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteInvoice(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Invoice deleted successfully.")

    return id
  }
)

export const fetchInvoiceSnapshot = createAsyncThunk<InvoiceSnapshot, string>(
  `${projectSliceName}/fetchInvoiceSnapshot`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getInvoiceSnapshot(projectId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as InvoiceSnapshot
  }
)
